/**
 * Migration chain: V1 → V2 → V3 → V4 → V5 → V6.
 *
 * The editor migrates on import and writes the current version on save. The
 * runtime NEVER migrates — it plays whatever version a session was started
 * under, full stop. This is the SQL-migrations pattern: boring on purpose,
 * because it means a learner's session from a year ago can still be
 * reproduced exactly.
 *
 * Every migration function is pure (input in, value out, no side effects).
 * `migrateIfNeeded` is the composition.
 */
import { newId } from './ids.js';
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * V1 → V2: a real reshape.
 *   - scene text/imageUrl on the node move into `variants[0]`
 *   - choice.destinationId moves into a single-entry `destinations` chain
 *   - choices get stable ids
 *   - scenario.variables is initialized to `{}`
 */
export function migrate_v1_to_v2(scenario) {
    const newNodes = {};
    for (const [id, n] of Object.entries(scenario.nodes || {})) {
        newNodes[id] = {
            id: n.id,
            title: n.title,
            type: n.type,
            x: n.x,
            y: n.y,
            variants: [{
                    id: newId('v'),
                    condition: null,
                    sceneText: n.sceneText || '',
                    imageUrl: n.imageUrl || '',
                }],
            choices: (n.choices || []).map((c) => ({
                id: newId('c'),
                label: c.label || '',
                destinations: [{ condition: null, destinationId: c.destinationId || null }],
                feedback: c.feedback || '',
            })),
            outcomeLabel: n.outcomeLabel || '',
        };
    }
    return {
        formatVersion: 2,
        title: scenario.title || 'Untitled scenario',
        startNodeId: scenario.startNodeId,
        variables: {},
        nodes: newNodes,
    };
}
/**
 * V2 → V3: purely additive — add an `objectives: {}` map. No fields are
 * reshaped. `objectiveTags` arrays are absent on every node and choice
 * (valid — the field is optional everywhere it appears).
 */
export function migrate_v2_to_v3(scenario) {
    return {
        ...scenario,
        formatVersion: 3,
        objectives: scenario.objectives || {},
    };
}
/**
 * V3 → V4: promote `choice.feedback` (a static string) to
 * `choice.feedbackVariants` (a first-match chain). Existing feedback strings
 * become the unconditional fallback entry; the `feedback` field is deleted.
 * No other fields move. Re-imports that already have feedbackVariants are
 * preserved (idempotent).
 */
export function migrate_v3_to_v4(scenario) {
    const newNodes = {};
    for (const [id, n] of Object.entries(scenario.nodes || {})) {
        newNodes[id] = {
            ...n,
            choices: (n.choices || []).map((c) => {
                // Idempotent: if the choice already has feedbackVariants, just strip any lingering `feedback` field.
                if (c.feedbackVariants && c.feedbackVariants.length > 0) {
                    const { feedback: _f, ...rest } = c;
                    return rest;
                }
                const { feedback, ...rest } = c;
                return {
                    ...rest,
                    feedbackVariants: [{
                            id: newId('vf'),
                            condition: null,
                            text: feedback || '',
                        }],
                };
            }),
        };
    }
    return {
        ...scenario,
        formatVersion: 4,
        nodes: newNodes,
    };
}
/**
 * V4 → V5: generalize each scene variant's single `imageUrl` string into an
 * ordered `media: MediaBlock[]` array. A non-empty `imageUrl` becomes a
 * one-element image block; an empty/absent `imageUrl` becomes an empty array.
 * The `imageUrl` field is deleted. No other fields move. Idempotent: variants
 * that already have `media` are left alone (any lingering `imageUrl` stripped).
 */
export function migrate_v4_to_v5(scenario) {
    const newNodes = {};
    for (const [id, n] of Object.entries(scenario.nodes || {})) {
        newNodes[id] = {
            ...n,
            variants: (n.variants || []).map((v) => {
                // Idempotent: if the variant already has media, just strip any lingering imageUrl.
                if (Array.isArray(v.media)) {
                    const { imageUrl: _img, ...rest } = v;
                    return rest;
                }
                const { imageUrl, ...rest } = v;
                const media = imageUrl
                    ? [{ id: newId('m'), kind: 'image', url: imageUrl }]
                    : [];
                return { ...rest, media };
            }),
        };
    }
    return {
        ...scenario,
        formatVersion: 5,
        nodes: newNodes,
    };
}
/**
 * V5 → V6: purely additive, the lightest migration in the chain. V6 adds three
 * OPTIONAL scenario-level clusters — `branding`, `intro`, `closing` — that drive
 * the title screen, end screen, and their theming. Because all three are
 * optional and absence is meaningful ("no title screen" / "no end screen" /
 * "default theme"), the migration deliberately does NOT seed them: seeding an
 * empty `intro` would make every existing scenario suddenly sprout a blank
 * title screen, which would violate the "absent = current behavior" contract.
 * So the only change is the version bump. Any `branding`/`intro`/`closing` that
 * happen to be present (e.g. a hand-authored or re-imported V6 file) pass
 * through untouched. Idempotent and pure.
 */
export function migrate_v5_to_v6(scenario) {
    return {
        ...scenario,
        formatVersion: 6,
    };
}
/**
 * V6 → V7. Adds optional `display` sizing (page size + text size). Like the
 * V5→V6 chrome fields, `display` is optional and absence is meaningful
 * ("responsive, exactly as before"), so the migration must NOT seed it —
 * seeding an empty `display` would be inert here but sets a bad precedent and
 * clutters serialized scenarios. The only change is the version bump. Any
 * `display` already present (a hand-authored or re-imported V7 file) passes
 * through untouched. Idempotent and pure.
 */
export function migrate_v6_to_v7(scenario) {
    return {
        ...scenario,
        formatVersion: 7,
    };
}
/**
 * Apply every migration needed to bring `parsed` up to V7. V7 inputs pass
 * through unchanged. Returns a properly-typed Scenario.
 */
export function migrateIfNeeded(parsed) {
    let cur = parsed;
    let v = cur.formatVersion || 1;
    if (v < 2) {
        cur = migrate_v1_to_v2(cur);
        v = 2;
    }
    if (v < 3) {
        cur = migrate_v2_to_v3(cur);
        v = 3;
    }
    if (v < 4) {
        cur = migrate_v3_to_v4(cur);
        v = 4;
    }
    if (v < 5) {
        cur = migrate_v4_to_v5(cur);
        v = 5;
    }
    if (v < 6) {
        cur = migrate_v5_to_v6(cur);
        v = 6;
    }
    if (v < 7) {
        cur = migrate_v6_to_v7(cur);
        v = 7;
    }
    return cur;
}
//# sourceMappingURL=migrations.js.map